import { useEffect, useMemo, useState } from "react";

import {
  listAssignableInstitutionAgents,
  setInstitutionAgent,
  type AgentSummary,
  type Institution,
} from "./api";

type Props = {
  institution: Institution;
  onClose: () => void;
  onSaved: (institution: Institution) => void;
};

function actionLabel(institution: Institution, nextAgent: AgentSummary | undefined): string {
  if (!nextAgent) return "确认解除绑定";
  return institution.agent ? "确认更换代理" : "确认绑定";
}

export function InstitutionAgentDialog({ institution, onClose, onSaved }: Props) {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(institution.agent?.id ?? "");
  const [stage, setStage] = useState<"select" | "confirm">("select");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    void listAssignableInstitutionAgents()
      .then(setAgents)
      .catch((reason) => setError(reason instanceof Error ? reason.message : "代理列表加载失败"))
      .finally(() => setLoading(false));
  }, []);

  const visibleAgents = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return agents;
    return agents.filter((agent) => (
      agent.name.toLowerCase().includes(term) || agent.mobile.includes(term)
    ));
  }, [agents, query]);
  const nextAgent = agents.find((agent) => agent.id === selectedId);
  const changed = selectedId !== (institution.agent?.id ?? "");

  async function confirm() {
    if (!changed || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      const updated = await setInstitutionAgent(institution.id, selectedId || null);
      onSaved(updated);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "关联代理失败，请稍后重试");
      setStage("select");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="platform-modal-layer" role="presentation">
      <section className="platform-modal platform-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="institution-agent-title">
        <div className="platform-modal-heading">
          <h2 id="institution-agent-title">关联代理</h2>
          <button aria-label="关闭" type="button" onClick={onClose}>×</button>
        </div>
        {stage === "select" ? <>
          <p>机构：<strong>{institution.name}</strong></p>
          <label>
            搜索代理
            <input
              autoFocus
              aria-label="搜索代理"
              placeholder="输入代理名称或手机号"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
          <label>
            选择代理
            <select
              aria-label="选择代理"
              disabled={loading}
              value={selectedId}
              onChange={(event) => setSelectedId(event.target.value)}
            >
              <option value="">暂不绑定</option>
              {visibleAgents.map((agent) => (
                <option key={agent.id} value={agent.id}>{agent.name} · {agent.mobile}</option>
              ))}
            </select>
          </label>
          {loading && <p>正在加载可关联代理…</p>}
          {!loading && agents.length === 0 && <p>暂无可关联的启用代理子账号。</p>}
          {error && <p className="platform-modal-error">{error}</p>}
          <div className="platform-modal-actions">
            <button className="is-secondary" type="button" onClick={onClose}>取消</button>
            <button className="platform-primary-button" type="button" disabled={!changed || loading} onClick={() => setStage("confirm")}>下一步</button>
          </div>
        </> : <>
          <h3>{actionLabel(institution, nextAgent)}</h3>
          <p>
            {nextAgent
              ? <>将 <strong>{institution.name}</strong> 关联至 <strong>{nextAgent.name}</strong>。</>
              : <>将解除 <strong>{institution.name}</strong> 当前的代理关联。</>}
          </p>
          <p>确认后，该机构当前产品的代理佣金将重置为 0.00；历史订单不会受影响。</p>
          {error && <p className="platform-modal-error">{error}</p>}
          <div className="platform-modal-actions">
            <button className="is-secondary" type="button" disabled={submitting} onClick={() => setStage("select")}>返回修改</button>
            <button className="platform-primary-button" type="button" disabled={submitting} onClick={() => void confirm()}>{actionLabel(institution, nextAgent)}</button>
          </div>
        </>}
      </section>
    </div>
  );
}
